import React, { useState } from 'react';
import { Box, Button, Divider, MenuItem, TextField, Typography } from '@mui/material';
import { IReservationPrice, KURTAXE_CATEGORIES, reservationPriceSchema } from '../Interfaces/IReservationPrice';
import { useCreateReservationPrice, useUpdateReservationPrice } from '../hooks/reservationsPrice';
import { useAlert } from '../core/AlertContext';
import { fieldSx, primaryButtonSx, secondaryButtonSx } from '../core/pvtaStyles';

interface SleepoverFeeEditProps {
    initialPrice: IReservationPrice;
    onClose: () => void;
    showHeading?: boolean;
}

const ReservationPriceEdit: React.FC<SleepoverFeeEditProps> = ({ initialPrice, onClose, showHeading = true }) => {
    const [priceItem, setPriceItem] = useState<IReservationPrice>(initialPrice);
    const isCreateMode = initialPrice.id < 0;
    const { createReservationPrice, isCreatingReservationPrice } = useCreateReservationPrice();
    const { updateReservationPrice, isUpdatingReservationPrice } = useUpdateReservationPrice();
    const { showAlert } = useAlert();
    const isSaving = isCreatingReservationPrice || isUpdatingReservationPrice;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setPriceItem((prev) => ({
            ...prev,
            [name]: name === 'price' ? Number(value) : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            const payload = reservationPriceSchema.pick({
                name: true,
                price: true,
                kurtaxe_category: true,
            }).parse(priceItem);

            if (isCreateMode) {
                await createReservationPrice(payload);
                showAlert({ text: 'Übernachtungspauschale erfolgreich angelegt.' });
            } else {
                await updateReservationPrice({ ...priceItem, ...payload });
                showAlert({ text: 'Übernachtungspauschale erfolgreich aktualisiert.' });
            }
            onClose();
        } catch {
            showAlert({
                text: isCreateMode
                    ? 'Fehler beim Anlegen der Übernachtungspauschale.'
                    : 'Fehler beim Aktualisieren der Übernachtungspauschale.',
                severity: 'error',
            });
        }
    };

    return (
        <Box sx={{ minWidth: 0 }}>
            <form onSubmit={handleSubmit}>
                {showHeading && (
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Details
                    </Typography>
                )}
                <TextField
                    name="name"
                    label="Name"
                    value={priceItem.name}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    size="small"
                    sx={{ ...fieldSx }}
                />
                <TextField
                    name="price"
                    label="Preis"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={priceItem.price}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    size="small"
                    sx={{ ...fieldSx }}
                />
                <TextField
                    name="kurtaxe_category"
                    label="Kurtaxe-Stufe"
                    select
                    value={priceItem.kurtaxe_category ?? ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    size="small"
                    helperText="Zuordnung zur Altersstufe für den Kurtaxe-Jahresexport."
                    sx={{ ...fieldSx }}
                >
                    {KURTAXE_CATEGORIES.map((option) => (
                        <MenuItem key={option.value || 'none'} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
                    <Button onClick={onClose} sx={{ ...secondaryButtonSx, width: { xs: '100%', sm: 'auto' } }}>
                        Schließen
                    </Button>
                    <Button
                        type="submit"
                        disabled={isSaving}
                        sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}
                    >
                        {isSaving ? 'Speichern...' : 'Speichern'}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default ReservationPriceEdit;
